import {useEffect, useMemo, useRef} from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {IconMapPin, IconX} from '@tabler/icons-react'
import {placePath} from './api/tree'
import type {TreeIndex} from './api/types'

interface Props {
  ix: TreeIndex
  /** сколько человек привязано к каждому месту — размер метки */
  usage: Map<string, number>
  /** поставить координаты месту: клик по карте в режиме расстановки */
  onPin: (handle: string, lat: string, long: string) => void
  onOpenPlace: (handle: string) => void
  /** место, которому сейчас ставим точку — задаётся из списка мест */
  pinning: string | null
  onPinning: (handle: string | null) => void
  /** что показать под картой после записи: успех или ошибка */
  status: {ok: boolean; text: string} | null
}

/* Leaflet тянет иконки маркеров отдельными файлами по относительному пути и
   в сборке их не находит. Рисуем свои — заодно они масштабируются по числу
   привязанных людей. */
const pin = (n: number, dim: boolean) => {
  const r = Math.min(22, 9 + Math.round(Math.sqrt(n) * 3))
  return L.divIcon({
    className: 'pinwrap',
    html: `<span class="pin${dim ? ' dim' : ''}" style="width:${r * 2}px;height:${r * 2}px">${n || ''}</span>`,
    iconSize: [r * 2, r * 2],
    iconAnchor: [r, r],
  })
}

/* Пустую строку разбирать нельзя: Number('') возвращает 0, а не NaN,
   и место без координат уезжало в точку (0, 0) — Гвинейский залив.
   Все четырнадцать садились туда разом, поверх океанской плитки. */
const num = (s: string): number | null => {
  const t = String(s ?? '').trim().replace(',', '.')
  if (!t) return null
  const v = Number(t)
  return Number.isFinite(v) ? v : null
}

export default function PlacesMap({ix, usage, onPin, onOpenPlace, pinning, onPinning, status}: Props) {
  const host = useRef<HTMLDivElement>(null)
  const map = useRef<L.Map | null>(null)
  const layer = useRef<L.LayerGroup | null>(null)
  /** место, которому сейчас ставим точку; null — обычный просмотр */
  const fitted = useRef('')
  /* Обработчики приходят стрелками из родителя и меняются каждый рендер.
     Держим их в ref, иначе карта пересоздавалась бы на каждый рендер,
     а метки перерисовывались и сбрасывали масштаб под руками. */
  const pinRef = useRef<string | null>(null)
  const onPinRef = useRef(onPin)
  const onOpenRef = useRef(onOpenPlace)
  const offRef = useRef(onPinning)
  pinRef.current = pinning
  onPinRef.current = onPin
  onOpenRef.current = onOpenPlace
  offRef.current = onPinning

  const dots = useMemo(() => {
    const out: {handle: string; lat: number; long: number; n: number}[] = []
    for (const [h, p] of ix.places) {
      const lat = num(p.lat)
      const long = num(p.long)
      if (lat === null || long === null) continue
      out.push({handle: h, lat, long, n: usage.get(h) || 0})
    }
    return out
  }, [ix, usage])

  const blank = useMemo(
    () => [...ix.places.entries()].filter(([, p]) => num(p.lat) === null || num(p.long) === null),
    [ix],
  )

  useEffect(() => {
    if (!host.current || map.current) return
    const m = L.map(host.current).setView([62, 80], 3)
    /* Подложка идёт через наш же nginx: браузер ходит только к нам,
       а сервер кэширует плитки у себя. Прямой доступ к tile.openstreetmap.org
       из России не везде работает — карта просто не прорисовывалась. */
    L.tileLayer('/tiles/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap',
    }).addTo(m)
    m.on('click', e => {
      const h = pinRef.current
      if (h) {
        onPinRef.current(h, e.latlng.lat.toFixed(6), e.latlng.lng.toFixed(6))
        offRef.current(null)   // одна точка за раз, иначе следующий клик её переставит
      }
    })
    layer.current = L.layerGroup().addTo(m)
    map.current = m

    /* Leaflet запоминает размер контейнера в момент создания. Если в этот
       миг коробка ещё не разложена — плитки садятся в один угол, и вместо
       карты получается точка. Пересчитываем после первой отрисовки и при
       любом изменении размеров. */
    const resize = () => m.invalidateSize()
    requestAnimationFrame(resize)
    const ro = new ResizeObserver(resize)
    ro.observe(host.current)
    window.addEventListener('resize', resize)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', resize)
      m.remove()
      map.current = null
    }
  }, [])

  useEffect(() => {
    const m = map.current
    const g = layer.current
    if (!m || !g) return
    g.clearLayers()
    for (const d of dots) {
      const name = placePath(ix, d.handle)
      L.marker([d.lat, d.long], {icon: pin(d.n, !d.n), title: name})
        .on('click', ev => { L.DomEvent.stop(ev); onOpenRef.current(d.handle) })
        .bindTooltip(`${name}${d.n ? ` — ${d.n} чел.` : ''}`)
        .addTo(g)
    }
    const key = dots.map(d => `${d.handle}:${d.lat}:${d.long}`).join('|')
    if (dots.length && key !== fitted.current) {
      fitted.current = key
      if (dots.length === 1) {
        // одна точка — fitBounds загоняет в предельное приближение,
        // где не видно ни города, ни ориентиров
        m.setView([dots[0].lat, dots[0].long], 11)
      } else {
        m.fitBounds(L.latLngBounds(dots.map(d => [d.lat, d.long] as [number, number])).pad(0.25))
      }
    }
  }, [dots, ix])

  useEffect(() => {
    host.current?.classList.toggle('pinning', !!pinning)
  }, [pinning])

  return (
    <div className="pmap">
      <div className="pmaphead">
        <div className="blabel">На карте</div>
        <div className="muted">
          {dots.length
            ? `${dots.length} из ${ix.places.size} мест с координатами`
            : 'ни у одного места пока нет координат'}
        </div>
        <div className="spacer" />
        {pinning ? (
          <>
            <span className="pinning">
              <IconMapPin size={14} stroke={1.9} />
              Щёлкните по карте — там и будет «{placePath(ix, pinning)}»
            </span>
            <button className="btn" onClick={() => onPinning(null)}>
              <IconX size={15} stroke={1.8} /> Отмена
            </button>
          </>
        ) : blank.length > 0 && (
          <select className="sel" value="" onChange={e => e.target.value && onPinning(e.target.value)}>
            <option value="">Отметить место на карте…</option>
            {blank.map(([h]) => (
              <option key={h} value={h}>{placePath(ix, h)}</option>
            ))}
          </select>
        )}
      </div>
      <div ref={host} className="pmapbox" />
      {/* Отклик держим у карты: общая полоса ошибок висит вверху страницы,
          а карта внизу — оттуда её просто не видно. */}
      {status && (
        <div className={status.ok ? 'okline' : 'err inline'}>{status.text}</div>
      )}
      {!dots.length && !status && (
        <div className="hint">
          <IconMapPin size={12} stroke={1.9} /> Ни у одного места нет координат. Нажмите
          <IconMapPin size={12} stroke={1.9} /> в строке места выше — и щёлкните по карте.
          Их же можно вписать руками при правке места.
        </div>
      )}
    </div>
  )
}
